<template>
    <AnalysisPane
      :community="community"
      :project="project"
      :get_kpi_fn="fetchExpressionKPIs"
    >
          <template v-if="kpis">
            <template v-if="kpis.audienceDiagram.length">
              <v-row class="project-cards__row row">
                  <v-col class="col-lg-12">
                      <ProjectCard
                              title="Top 5 sources"
                              hint>
                              <v-row align="start">
                                <v-col cols="10">
                                  <Plotly :data="lplotlifyAD(kpis.audienceDiagram)" :layout="audienceDiagramLayout()"/>
                                </v-col>
                                <v-col cols="2" class="d-flex flex-column-reverse">
                                  <v-btn v-for="(e, i) in kpis.audienceDiagram" :color="plotColor[i]" dark small class="my-2"
                                    :href="'https://twitter.com/' + e.label" target="_blank">{{ e.label }}</v-btn>
                                </v-col>
                              </v-row>
                          <template v-slot:tooltip>
                              Top 5 sources are computed as users who reached
                              (were retweeted, quoted or replied to) by the most
                              of Community users. For those top 5 users, the
                              fraction of community members reached per day was
                              computed.

                              If field of expression is provied sources are calculated
                              only with messages matching the specified expression.
                          </template>
                          <template v-slot:hint>
                              <!-- Top source triggered {{ round(kpis.audienceDiagram[0].data.slice(-1)[0].y) }}% of messages -->
                              The top 5 sources triggered messages.
                          </template>
                      </ProjectCard>
                  </v-col>
              </v-row>

              <v-row class="project-cards__row row">
                  <v-col class="col-lg-6">
                      <ProjectCard
                              title="Activity by decile group"
                              hint>
                          <Plotly :data="plotlify(kpis.chattinessByDecile)" :layout="chattinessDecileLayout()"/>
                          <template v-slot:tooltip>
                              After ranking members from the community by activity, we divided
                              them in ten groups of equal size and show the average number of
                              messages published per member for each group.
                          </template>
                          <template v-slot:hint>
                              The 10% most active members of the group published
                              {{ round(kpis.chattinessByDecile.Community[9].y) }}
                              messages on average.
                          </template>
                      </ProjectCard>
                  </v-col>
                  <v-col class="col-lg-6">
                      <ProjectCard
                              title="Activity"
                              hint>
                          <template v-slot:tooltip>
                              Activity is the number of messages published per member per day.
                              Overall average activity computed by dividing the number of
                              messages published by a community by its member size.

                              If field of expression is provied the activity is calculated
                              only with messages matching the specified expression.
                          </template>
                          <div>
                              <span> Community average daily messages: {{ round2(kpis.overallChattinessOverTime.Community.average) }}</span><br>
                              <span> Panel average daily messages: {{ round2(kpis.overallChattinessOverTime.Panel.average) }}</span>
                          </div>
                          <Plotly :data="lplotlify(kpis.overallChattinessOverTime)" :layout="chattinessInTimeLayout()"/>

                          <template v-slot:hint>
                              Members of the community publish an average of
                              {{ round(kpis.overallChattiness.Community) }}
                              messages per day.
                          </template>
                      </ProjectCard>
                  </v-col>
              </v-row>

              <v-row class="project-cards__row row">
                  <v-col class="col-lg-6">
                      <ProjectCard
                              title="Top 10 keywords"
                              hint>
                          <template v-slot:tooltip>
                              Top 10 most widely used words in the community, excluding most
                              common words. For each of these keywords, we then display the 10
                              most associated words.

                              Top 10 ranking is based on how often these words occur in messages
                              published by the community.
                          </template>
                          <table class="topkeywords">
                              <tr v-for="kw in (kpis.topKeywords.entries())" :key="kw">
                                  <td valign="top">
                                      <div class="keyword">{{kw[1].name}}</div>
                                      <div class="keyword-pcnt">{{round(kw[1].pcnt_users)}}% of members</div>
                                  </td>
                                  <td valign="bottom" class="subkeywords">
                                      {{kw[1].words.join(", ")}}
                                  </td>
                              </tr>
                          </table>
                          <template v-slot:hint>
                              "{{ kpis.topKeywords[0].name }}"" was used by
                              {{ round(kpis.topKeywords[0].pcnt_users) }}% of members
                          </template>

                      </ProjectCard>
                  </v-col>
                  <v-col class="col-lg-6">
                      <ProjectCard
                              title="Activity distribution"
                              hint>
                          <template v-slot:tooltip>
                              Activity is the average number of messages published per member
                              per day. Average activity is computed by dividing the number of
                              messages published by a community by its member size.

                              If field of expression is provied the activity is calculated
                              only with messages matching the specified expression.
                          </template>
                          <Plotly :data="plotlify(kpis.chattinessDistribution)" :layout="chattinessDistributionLayout()"/>
                          <template v-slot:hint>
                              {{ hintForActivity(kpis.chattinessDistribution) }}
                          </template>
                      </ProjectCard>
                  </v-col>
              </v-row>

              <v-row class="project-cards__row row">
                  <v-col class="col-lg-6">
                      <ProjectCard
                              title="Top URLs"
                              hint>
                          <template v-slot:tooltip>
                              3 most shared URLs among the messages published by the community
                          </template>
                          <table>
                              <tr v-for="(kw, i) in (kpis.mostTweetedURLPodium.entries())" :key="kw">
                                  <td valign="top">{{ i+1 }}</td>
                                  <td valign="top"><b><a :href="kw[1].url">{{kw[1].url}}</a> </b></td>
                                  <td valign="top"> {{round2(kw[1].value)}} %</td>
                              </tr>
                          </table>
                          <template v-slot:hint>
                              The most shared URL was broadcast by {{ round2(kpis.mostTweetedURLPodium[0].value) }}% of the community.
                          </template>
                      </ProjectCard>
                  </v-col>
              </v-row>
            </template>
            <template v-else>
              <v-row class="project-cards__row row">
                  <v-col class="col-lg-6">
                  <ProjectCard title="No data found" >
                  Cannot find any documents matching given criteria. Please change your filters or timeframe
                      <template v-slot:tooltip>
                          Expression uses filter criteria to
                          find matching documents. For given
                          ones there are no documents matching.
                      </template>
                  </ProjectCard>
                  </v-col>
              </v-row>
            </template>
          </template>
    </AnalysisPane>
</template>

<script>
    import _ from "lodash";
    import ProjectCard from "./../ProjectCard";
    import CommunityService from "../../../services/community.service";
    import {VueSvgGauge} from 'vue-svg-gauge'
    import {Plotly} from 'vue-plotly'
    import AnalysisPane from "./AnalysisPane"

    export default {
        name: "Expression",
        components: {ProjectCard, VueSvgGauge, Plotly, AnalysisPane},
        props: ["community", "project", "fieldOfExpression"],
        data: () => ({
            kpis: null,
            // TODO nicer colors, use it in plot too
            plotColor: [ "#3976AF", "#F08536", "#519D3E", "#C63A32", "#8D6BB8" ]
        }),
        methods: {
            stickman(c) {
                let k = _.min([Math.floor(c / 10), 101])
                return _.range(k)
            },
            _plotlify(d, name) {
                return {
                    x: _.map(d, e => e.x),
                    y: _.map(d, e => e.y),
                    type: "bar",
                    name: name,
                    marker: {color: name === 'Community' ? '#0499fe' : '#e3e3e3'},
                    hovertemplate: "%{y:.2f}%"
                }
            },
            _hplotlify(d, name) {
                return {
                    y: _.map(d, e => e.x),
                    x: _.map(d, e => e.y),
                    type: "bar",
                    orientation: "h",
                    marker: {color: name === 'Community' ? '#0499fe' : '#e3e3e3'},
                    name: name,
                    hovertemplate: "%{y:.2f}%"
                }
            },
            _lStackPlotlify(d, name) {
                return {
                    x: _.map(d, e => e.x),
                    y: _.map(d, e => e.y),
                    type: "scatter",
                    stackgroup: "one",
                    name: name,
                    hovertemplate: "%{y:.2f}%"
                }
            },
            _lPlotlifyAD(d, name) {
                return {
                    x: _.map(d, e => e.x),
                    y: _.map(d, e => e.y),
                    type: "bar",
                    name: name,
                  hovertemplate: "%{y:.2f}%",
                }
            },
            _lPlotlifyKW(d, name) {
                return {
                    x: _.map(d, e => e.x),
                    y: _.map(d, e => e.y),
                    type: "scatter",
                    name: name,
                    hovertemplate: "%{y:.2f}%"
                }
            },
            _lPlotlify(d, name) {
                return {
                    x: _.map(d.data, e => e.x),
                    y: _.map(d.data, e => e.y),
                    type: "scatter",
                  name: name,
                  hovertemplate: "%{y:.2f}"
                }
            },
            plotlify(d) {
                return d ? [this._plotlify(d.Community, "Community"),
                    this._plotlify(d.Panel, "Panel")] : []
            },
            hplotlify(d) {
                return d ? [this._hplotlify(d.Community, "Community"),
                    this._hplotlify(d.Panel, "Panel")] : []
            },
            lsplotlify(d) {
                return d ? _.map(d, e => this._lStackPlotlify(e.data, e.label)) : []
            },
            lplotlifyAD(d) {
                console.log("chatiness over time: ", d);
                return d ? _.map(d, e => this._lPlotlifyAD(e.data, e.label)) : []
            },
            lplotlifyKW(d) {
                return d ? _.map(d, e => this._lPlotlifyKW(e.data, e.label)) : []
            },
            lplotlify(d) {
                console.log(d)
                return d ? [this._lPlotlify(d.Community, "Community"),
                    this._lPlotlify(d.Panel, "Panel")] : []
            },
            round(x) {
                return Number.parseFloat(x).toFixed(1)
            },
            round2(x) {
                return Number.parseFloat(x).toFixed(2)
            },
            audienceDiagramLayout() {
                return {
                    yaxis: {
                        title: "% members"
                    },
                    barmode: 'stack',
                    hovermode: 'closest',
                    showlegend: false
                }
            },
            top5HashtagsLayout() {
                return {
                    yaxis: {
                        title: "% of messages published by the community"
                    },
                    hovermode: 'closest'
                }
            },
            chattinessDecileLayout() {
                return {
                    yaxis: {
                        title: "% messages"
                    },
                    xaxis: {
                        title: "Decile groups"
                    },
                    hovermode: 'closest'
                }
            },
            chattinessDistributionLayout() {
                return {
                    yaxis: {
                        title: "% of members"
                    },
                    xaxis: {
                        title: "Average daily messages"
                    },
                    hovermode: 'closest'
                }
            },
            chattinessInTimeLayout() {
                return {
                    yaxis: {
                        title: "Average daily messages"
                    },
                    hovermode: 'closest'
                }
            },
            hintForActivity(c) {
                let cmax = _.maxBy(c.Community, v => v.y)
                if (cmax.x === "<1") {
                    return `The majority of members (${this.round(cmax.y)}%) publish less than 1 message per day.`
                } else if (cmax.x === "1-5") {
                    return `The majority of members (${this.round(cmax.y)}%) publish between 1 and 3 messages per day.`
                } else {
                    return `The majority of members (${this.round(cmax.y)}%) publish more than 5 messages per day.`
                }
            },
            fetchExpressionKPIs() {
              var cs = new CommunityService();
              return cs.getExpressionKPIs(
                  {
                      startDate: this.project.timeframe_start,
                      endDate: this.project.timeframe_end,
                      query: this.community,
                      field_of_expression: this.fieldOfExpression,
                  },
                  null
              ).then(resp => {
                  // Only 200 gives real data
                  if (resp.status == 200) {
                    this.kpis = resp.data
                  }
                  return resp;
              })
            }
        },
    }
</script>

<style scoped>
    .inner-text {
        height: 100%;
        width: 100%;
        margin-top: 85px

    }

    .inner-gauge {
        height: 100%;
        width: 50%;
        margin-top: 50px;
        display: flex;
        align: center;
        align-items: center;
        justify-content: center

    }

    .inner-text span {
        max-width: 200px;
        display: flex;
        align-items: center;
        justify-content: center
    }

    .kpi-value {
        align-items: center;
        justify-content: center;
        text-align: center
    }

    .topkeywords td {
        padding-left: 10px;
        padding-right: 10px;
    }
    .keyword {
        color: #0499fe;
        font-size: 240%;
    }
    .subkeywords {
        color: #8798ad;
    }
</style>
